<template>
  <div class="card" v-show="currentCardId == 'requests'">
    <div class="overflow-scroll-y">
      <div class="card-header">
        <h2>本人希望欄を入力</h2>
        <p>給与・職種・勤務時間・勤務地などの希望を入力します（任意）</p>
        <p v-if="formatId == 'rirekisho_job_change_old'">
          ですが、<span style="color: #e3165c"
            >基本的には面接で話されることが多いため、「貴社規定に従います。」と記載しておくことが一般的</span
          >です。
        </p>
      </div>
      <div class="add-item">
        <el-button type="success" @click="exampleVisible = true"
          >例文から追加</el-button
        >
      </div>
      <div class="card-content">
        <template v-if="isSalaryVariant">
          <div class="edit-form-item">
            <div class="item-name">現在の年収</div>
            <div class="item-body">
              <el-input
                class="input-xs"
                placeholder=""
                title="現在の年収"
                type="number"
                v-model="formatData.nowNensyu"
              />
              万円
            </div>
            <div class="item-name">希望年収</div>
            <div class="item-body">
              <el-input
                class="input-xs"
                placeholder=""
                title="希望年収"
                type="number"
                v-model="formatData.kibouNensyu"
              />
              万円
            </div>
            <div class="item-name">
              ※上記項目を入力した後、例文から追加ボタンをクリックして例文を選択すると自動的に反映されます。
            </div>
          </div>
          <br />
        </template>
        <PersonalRequestFields
          v-if="!isSalaryVariant"
          mode="fields"
          :format-data="formatData"
          :basic-data="basicData"
          :format-id="formatId || ''"
          :industry-data="industryData"
          :job-type-data="jobTypeData"
          :show-ai-card="true"
          @update-basic-data="onUpdateBasicData"
          @update-format-data="Object.assign(formatData, $event)"
        />
        <NewRirekishoExampleSentenceDialog
          :value="exampleVisible"
          name="request-dialog"
          :items="requestExamples"
          show-overwrite-warning
          overwrite-warning="例文を選択すると、現在入力している文章は上書きされますのでご注意ください。"
          @input="exampleVisible = $event"
          @select="onSelectExample"
        />
        <el-input
          type="textarea"
          :autosize="{ minRows: 3, maxRows: 15 }"
          placeholder="給与・職種・勤務時間・勤務地など希望があれば入力"
          v-model="formatData.request"
          v-proofread
        />
        <PersonalRequestFields
          v-if="!isSalaryVariant"
          mode="ai-card"
          :format-data="formatData"
          :basic-data="basicData"
          :format-id="formatId || ''"
          :industry-data="industryData"
          :job-type-data="jobTypeData"
          :show-ai-card="true"
          :request-ai-generated="requestAiGenerated"
          :request-ai-loading="requestAiLoading"
          :request-ai-error="requestAiError"
          :bizreach-industry-groups="bizreachIndustryGroups"
          :bizreach-job-category-groups="bizreachJobCategoryGroups"
          :get-industry-name="getIndustryName"
          :get-job-category-name="getJobCategoryName"
          @update-basic-data="onUpdateBasicData"
          @update-format-data="Object.assign(formatData, $event)"
        />
        <div class="attention-limit character" v-if="previewErrors.request">
          <i class="el-icon-warning"></i>
          入力可能な文字数をオーバーしています。文章を短くするか、入力欄の大きい別の履歴書を選んでください。
        </div>
      </div>
      <div v-if="formatId == 'rirekisho_job_change_old'">
        <p class="guidetxt">本人希望欄の見本（参考に作成しましょう）</p>
        <img
          src="/static/img/rirekishoguide/guide08.png"
          alt="本人希望欄"
          class="guideimg"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { industryData } from '@/assets/formats/industryData'
import { jobTypeData } from '@/assets/formats/jobTypeData'
import PersonalRequestFields from '@/components/formats/RirekishoParts/PersonalRequestFields.vue'
import NewRirekishoExampleSentenceDialog from '@/components/format-edit/new-rirekisho/NewRirekishoExampleSentenceDialog.vue'
import {
  useRirekishoFormatData,
  type RirekishoExampleSentence,
  type RirekishoExampleSentences
} from '@/composables/format-edit/useRirekishoFormatData'
import { useRirekishoRequestAi } from '@/composables/format-edit/useRirekishoRequestAi'
import type { BasicData } from '@/utils/format-edit/basicData'

const props = withDefaults(
  defineProps<{
    currentCardId?: string
    formatId?: string
    basicData: BasicData
    previewErrors: Record<string, string>
    exampleSentences?: RirekishoExampleSentences
    variant?: 'full' | 'salary'
  }>(),
  {
    variant: 'full'
  }
)

const emit = defineEmits<{
  (e: 'update-basic-data', payload: Partial<BasicData>): void
}>()

const formatData = useRirekishoFormatData()
const exampleVisible = ref(false)
const isSalaryVariant = computed(() => props.variant === 'salary')

const {
  requestAiGenerated,
  requestAiLoading,
  requestAiError,
  bizreachIndustryGroups,
  bizreachJobCategoryGroups,
  getIndustryName,
  getJobCategoryName,
  runAutoRequestIfNeeded
} = useRirekishoRequestAi({
  formatData,
  basicData: () => props.basicData,
  emitUpdateBasicData: (payload) => emit('update-basic-data', payload),
  enabled: props.variant !== 'salary'
})

const requestExamples = computed(() => props.exampleSentences?.request || [])

watch(
  () => props.currentCardId,
  (newVal) => {
    if (newVal === 'requests' && !isSalaryVariant.value) {
      runAutoRequestIfNeeded()
    }
  }
)

function onUpdateBasicData(payload: Partial<BasicData>) {
  Object.assign(props.basicData, payload)
  emit('update-basic-data', payload)
}

function onSelectExample(item: RirekishoExampleSentence) {
  formatData.request +=
    item.description
      .replace(/<br>/gi, '\n')
      .replace('OOO', formatData.nowNensyu)
      .replace('XXX', formatData.kibouNensyu) + '\n'
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/format-edit/rirekisho-guide';
</style>
